import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "fileInput",
    "uploadedEmailsInput",
    "fileContent",
    "fileName",
    "emailsContent",
    "resultCount",
    "invalidEmails",
    "downloadInvalidEmails",
    "errorMessage"
  ]

  static values = {
    communitySpaceId: String,
    parseUrl: String,
    successLabel: String
  }

  initialize() {
    this.clearFileContent = this.clearFileContent.bind(this)
  }

  connect() {
    document.addEventListener("invite_users:invite_option_changed", this.clearFileContent)
  }

  disconnect() {
    document.removeEventListener("invite_users:invite_option_changed", this.clearFileContent)
  }

  triggerFileInput() {
    this.fileInputTarget.click()
    this.clearError()
  }

  parseFile() {
    const file = this.fileInputTarget.files[0]
    if (!file) return

    this.fileContentTarget.classList.remove("hidden")
    this.fileNameTarget.textContent = file.name

    const formData = new FormData()
    formData.append("file", file)

    if (this.hasCommunitySpaceIdValue && this.communitySpaceIdValue) {
      formData.append("community_space_id", this.communitySpaceIdValue)
    }

    fetch(this.parseUrlValue, {
      method: "POST",
      body: formData,
      headers: this.requestHeaders
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(errorData => {
            throw new Error(errorData.error || "An error occurred while parsing the file.")
          })
        }
        return response.json()
      })
      .then(data => {
        this.displayValidationResults(data)
      })
      .catch(error => {
        this.displayError(error.message)
        this.clearFileContent()
      })
  }

  displayValidationResults(data) {
    const { valid_emails, accepted_emails_count, invalid_emails, error } = data

    this.invalidEmailsList = invalid_emails
    this.emailsContentTarget.classList.remove("hidden")

    if (this.hasUploadedEmailsInputTarget) {
      this.uploadedEmailsInputTarget.value = valid_emails.join(",")
    }

    if (accepted_emails_count) {
      this.resultCountTarget.textContent = `${this.successLabelValue}: ${accepted_emails_count}`
    }

    if (invalid_emails.length) {
      this.invalidEmailsTarget.textContent = `Invalid emails: ${invalid_emails.length}`
      this.downloadInvalidEmailsTarget.classList.remove("hidden")
    }

    if (error) {
      this.displayError(error)
    }

    this.dispatchEmailsUploadedEvent({
      acceptedCount: accepted_emails_count,
      invalidCount: invalid_emails.length
    })
  }

  downloadInvalidEmailsCsv() {
    if (!this.invalidEmailsList?.length) return

    const csvContent = this.invalidEmailsList.join("\n")
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")

    link.href = url
    link.download = "invalid_emails.csv"
    link.click()

    URL.revokeObjectURL(url)
  }

  displayError(errorMessage) {
    this.errorMessageTarget.classList.remove("hidden")
    this.errorMessageTarget.textContent = errorMessage
  }

  clearError() {
    this.errorMessageTarget.classList.add("hidden")
    this.errorMessageTarget.textContent = ""
  }

  clearFileContent() {
    this.fileInputTarget.value = ""
    this.fileNameTarget.textContent = ""
    this.resultCountTarget.textContent = ""
    this.invalidEmailsTarget.textContent = ""
    this.downloadInvalidEmailsTarget.classList.add("hidden")
    this.invalidEmailsList = []
    this.fileContentTarget.classList.add("hidden")
    this.emailsContentTarget.classList.add("hidden")

    if (this.hasUploadedEmailsInputTarget) {
      this.uploadedEmailsInputTarget.value = ""
    }

    this.dispatchClearFileContentEvent()
  }

  get requestHeaders() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    return csrfToken ? { "X-CSRF-Token": csrfToken } : {}
  }

  dispatchEmailsUploadedEvent(detail) {
    const event = new CustomEvent("parse_emails_file:emails_uploaded", {
      bubbles: true,
      detail
    })
    document.dispatchEvent(event)
  }

  dispatchClearFileContentEvent() {
    const event = new CustomEvent("parse_emails_file:clear_file_content", { bubbles: true })
    document.dispatchEvent(event)
  }
}
